import { NavigationContainer } from '@react-navigation/native';
import React, { FC, useMemo } from 'react';
import { ThemeProvider, createGlobalStyle } from 'styled-components';
import { ThemeProvider as NativeThemeProvider } from 'styled-components/native';

import { PORTAL_GATE_ALERT } from '@webtrader/core/constants/portal';
import { useCompanySettings } from '@webtrader/core/hooks/company';
import { useUpdateLanguageOnLoadWeb } from '@webtrader/core/hooks/language';
import { useTranslation } from '@webtrader/core/i18n';
import { useAuthWithKeys } from '@webtrader/core/services/auth';
import { useTheme } from '@webtrader/core/services/theme/v2';
import { useSyncLanguage } from '@webtrader/core/syncing/language';
import { useSyncTradingAccount } from '@webtrader/core/syncing/tradingAccount';

import { PortalGate } from '@/components/Portal/PortalGate';
import { PortalProvider } from '@/components/Portal/PortalProvider';
import StatusBar from '@/components/StatusBar';
import { navigationRef } from '@/utils/navigation';
import { useRouteQueryParamsSearch } from '@/utils/navigation.web';

import Navigation from './navigation';

const Screens: FC = () => {
  const { lang: languageQueryString } = useRouteQueryParamsSearch();
  const {
    i18n: { language }
  } = useTranslation();
  useSyncLanguage(language);

  const { selectedTradingAccount } = useAuthWithKeys(['selectedTradingAccount']);

  useSyncTradingAccount(selectedTradingAccount);

  useUpdateLanguageOnLoadWeb({ languageQueryString });

  const { theme, ready } = useTheme();

  const { company } = useCompanySettings();

  const title = useMemo(() => {
    if (!company?.name) {
      return 'WebTrader Platform';
    }
    return `${company?.name} - WebTrader Platform`;
  }, [company?.name]);

  if (!ready) {
    return null;
  }

  return (
    <NavigationContainer
      ref={navigationRef}
      documentTitle={{
        formatter: () => `${title}`
      }}
    >
      <NativeThemeProvider theme={theme}>
        <ThemeProvider theme={theme}>
          <PortalProvider>
            <PortalGate gateName={PORTAL_GATE_ALERT} />
            <StatusBar />
            <Navigation />
            <GlobalStyles />
          </PortalProvider>
        </ThemeProvider>
      </NativeThemeProvider>
    </NavigationContainer>
  );
};

export default Screens;

const GlobalStyles = createGlobalStyle`
  &.datepicker.android, .datepicker.android-dark {
    background-color: ${({ theme }) => theme.colors.background[0]} !important;
  }

  &.datepicker.android .datepicker-viewport::after, .datepicker.android-dark .datepicker-viewport::after {
    background-image: linear-gradient(${({ theme }) =>
      theme.colors.background[0]},rgba(40, 40, 40, 0) 52%,rgba(40, 40, 40, 0) 48%,${({ theme }) =>
  theme.colors.background[0]}) !important;
  }

  @-moz-document url-prefix() {
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
      filter: none;
      box-shadow: inset 0 0 0 1000px ${({ theme }) => theme.colors.background[0]};
    }
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    border: none;
    box-shadow: ${({ theme }) =>
      `0 0 0 100px ${theme.colors.background[0]} inset !important, 0 0 1px ${theme.colors.border[1]}`};
    transition: background-color 99999s ease-in-out 0s;
    -webkit-text-fill-color: ${({ theme }) => `${theme.colors.text[0]} !important`};
  }
`;
